import React from "react";
import T from "prop-types";
import classNames from "classnames";
import _ from "lodash";
import { push } from "react-router-redux";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { shortDateYY } from "LIB/formatters/date";
import { getFirstNameLastInitial } from "LIB/formatters/text";
import { dollars } from "LIB/formatters/currency";
import { displayDecimal } from "LIB/formatters/number";
import { condition } from "MODULES/SalesOrders/constants";
import {
  CompanyName,
  Icon,
  NoteTooltip as IconTooltip,
  PartNumber
} from "MODULES/shared/components";
import { COST_ADJUSTMENT_OPTIONS } from "MODULES/inventories/constants";
import AuditedNoteTooltip from "MODULES/shared/components/NoteTooltip/AuditedNoteTooltip";
import RowActions from "./RowActions";
import { toggleLock as toggle } from "../../actions";

function StatusBadge({ status }) {
  switch (status) {
    case "paid":
      return (
        <span className="label label-success">
          <i className="fa fa-check" />
          &nbsp;&nbsp; Paid
        </span>
      );
    case "pending":
      return (
        <span className="label label-warning">
          <i className="fa fa-pause" />
          &nbsp;&nbsp; Pending
        </span>
      );
    case "bad_debt":
      return (
        <span className="label label-danger">
          <i className="fa fa-warning" />
          &nbsp;&nbsp; Bad Debt
        </span>
      );
    case "reversed":
      return (
        <span className="label label-success">
          <i className="fa fa-check" />
          &nbsp;&nbsp; Reversed
        </span>
      );
    default:
      return false;
  }
}

StatusBadge.propTypes = {
  status: T.string.isRequired
};

export const costAdjLabel = (c, depreciationCommission) => {
  if (depreciationCommission) {
    return "Depreciation (-)";
  } else if (c.cost_adjustment_id) {
    return COST_ADJUSTMENT_OPTIONS[c.adjustment_type].abbrev;
  } else if (c.reversal) {
    return "SO Credit";
  }

  if (c.managed_repair_order_line_item_id) {
    return "MRO";
  }
  return "SO";
};

function Row({
  commission,
  canEdit,
  pushHistory,
  setCommission,
  setUpdateStatusModalOpen,
  setUpdateOverrideOpen
}) {
  const c = commission;

  const depreciationCommission =
    !c.reference_cost_adj_sales_item_id && c.cost_adjustment_id;

  return (
    <tr key={c.id} className="clickable" onClick={() => pushHistory(c)}>
      <td>{shortDateYY(c.sales_date)}</td>
      <td>
        {_.get(c, "sales_order.code", _.get(c, "managed_repair_order.code"))}
      </td>
      <td>{costAdjLabel(c, depreciationCommission)}</td>
      {canEdit && (
        <td>
          {c.team_leader && (
            <span>
              <IconTooltip icon="fa-users" note="Team Leader" />&nbsp;&nbsp;
            </span>
          )}
          {getFirstNameLastInitial(c.employee.full_name)}
        </td>
      )}
      <td>
        <CompanyName flattenedCompany={c} />
      </td>
      <td>
        <PartNumber
          part={{
            id: c.part_id,
            part_number: c.part_number,
            tier_type: c.part_tier_type,
            uuid: c.part_uuid
          }}
        />
      </td>
      <td>
        <Link
          onClick={e => e.stopPropagation()}
          to={`/inventories/${c.inventory.uuid || c.inventory_id}`}
        >
          {c.serial_number}
        </Link>
      </td>
      <td>{condition(c.condition).abbrev}</td>
      <td className="text-right">{dollars(c.cost)}</td>
      <td className="text-center">{c.quantity}</td>
      <td className="text-right">{dollars(c.sale_price)}</td>
      <td className="text-right">{dollars(c.profit)}</td>
      <td>{c.seller && getFirstNameLastInitial(c.seller.full_name)}</td>
      <td>{getFirstNameLastInitial(_.get(c, "buyer.full_name"))}</td>
      <td>{getFirstNameLastInitial(_.get(c, "co_buyer.full_name"))}</td>
      <td>{getFirstNameLastInitial(_.get(c, "third_buyer.full_name"))}</td>
      <td>{c.customer_purchase_order_number}</td>
      <td>{c.invoice_number}</td>
      <td>
        {shortDateYY(
          depreciationCommission ? c.created_at : c.invoice_paid_date
        )}
      </td>
      <td className="text-right">{shortDateYY(c.paid_date)}</td>
      <td>
        <StatusBadge status={c.status} />
        <AuditedNoteTooltip obj={c} field="status" />
      </td>
      <td className="text-right">
        {displayDecimal(c.sales_credit_percent || c.credit_percent, 10)}%
      </td>
      <td className="text-right">{dollars(c.sales_credit)}</td>
      <td className="text-right">{dollars(c.profit_credit)}</td>
      <td className="text-right">
        {c.total_commission && dollars(c.total_commission)}{" "}
        <AuditedNoteTooltip obj={c} field="overridden_value" />
      </td>
      <td className="text-center">
        {c.freight_complete || c.cost_adjustment_id ? "Yes" : "No"}
      </td>
      {canEdit && (
        <td>
          <RowActions
            commission={c}
            setCommission={setCommission}
            setUpdateStatusModalOpen={setUpdateStatusModalOpen}
            setUpdateOverrideOpen={setUpdateOverrideOpen}
          />
        </td>
      )}
    </tr>
  );
}

function LockedButton({ commission, onClick }) {
  const classes = classNames({
    "text-danger": commission.locked && !commission.commissions_paid,
    "text-success": !commission.locked && !commission.commissions_paid,
    "text-muted": commission.commissions_paid
  });

  return (
    <td
      className={classes}
      onClick={commission.commissions_paid ? e => e.stopPropagation() : onClick}
    >
      <Icon name={commission.locked ? "lock" : "unlock"} />
    </td>
  );
}

LockedButton.propTypes = {
  commission: T.shape({}),
  onClick: T.func.isRequired
};

Row.propTypes = {
  canEdit: T.bool,
  pushHistory: T.func.isRequired,
  commission: T.shape({
    condition: T.string.isRequired,
    cost: T.string.isRequired,
    customer_paid: T.string.isRequired,
    locked: T.bool.isRequired,
    profit: T.number.isRequired,
    profit_credit: T.string.isRequired,
    sale_price: T.string.isRequired,
    sales_date: T.string.isRequired,
    sales_order_code: T.string.isRequired,
    positive_credit_percent: T.number.isRequired,
    seller: T.shape({
      full_name: T.string.isRequired
    }).isRequired,
    serial_number: T.string.isRequired,
    status: T.string.isRequired,
    sales_credit: T.string.isRequired,
    total_commission: T.string
  }).isRequired,
  manager: T.shape({})
};

export default connect(undefined, dispatch => ({
  toggleLock: commission => dispatch(toggle(commission)),
  pushHistory: commission => {
    const url = commission.cost_adjustment_id
      ? `/inventories/${commission.inventory.uuid || commission.inventory_id}`
      : `/sales-orders/${commission.sales_order.uuid}`;

    return dispatch(push(url));
  }
}))(Row);



// WEBPACK FOOTER //
// ./app/modules/Commissions/components/Search/Row.jsx